import * as React from "react";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { StarField } from "@/components/StarField";
import { useInView } from "@/hooks/useInView";

const FAQS = [
  {
    q: "Was kostet die Website wirklich – gibt es versteckte Kosten?",
    a: "Nein. Du zahlst den monatlichen Paketpreis ab 59 € netto. Hosting, SSL, Wartung und die vereinbarten Anpassungen sind enthalten. Einrichtungsgebühren gibt es nicht.",
  },
  {
    q: "Wie lange läuft der Vertrag?",
    a: "Die Pakete laufen monatlich und sind monatlich kündbar. Du kannst jederzeit in ein größeres Paket wechseln, wenn dein Unternehmen mehr Inhalte braucht.",
  },
  {
    q: "Wann ist meine Website online?",
    a: "Den ersten individuellen Entwurf siehst du in der Regel innerhalb von 14 Tagen. Nach deiner Freigabe geht die fertige Seite meist innerhalb von einer bis drei Wochen live.",
  },
  {
    q: "Was passiert mit meiner bestehenden Domain?",
    a: "Wir übernehmen den Umzug. Deine Domain bleibt dir, wir verbinden sie mit der neuen Website und prüfen Weiterleitungen und E-Mail-Einstellungen.",
  },
  {
    q: "Muss ich Texte und Bilder selbst liefern?",
    a: "Du kannst, musst aber nicht. Wenn Material fehlt, strukturieren wir deine Inhalte gemeinsam und arbeiten mit Platzhaltern, bis alles passt.",
  },
  {
    q: "Gehört die Website mir?",
    a: "Design und Inhalte sind deine. Solange du im Abo bist, kümmern wir uns um Technik, Hosting und Pflege – du hast jederzeit Zugriff auf deine Inhalte.",
  },
  {
    q: "Ist die Demo wirklich kostenlos?",
    a: "Ja. Du siehst einen echten Entwurf für dein Unternehmen, bevor du dich entscheidest. Erst wenn dir das Design gefällt, wird es konkret.",
  },
  {
    q: "Können später Google Ads, SEO oder Meta Ads dazukommen?",
    a: "Jederzeit. Die weiteren Leistungen sind einzeln buchbar und lassen sich flexibel zu deinem Website-Paket ergänzen.",
  },
];

export const FaqSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08 });

  return (
    <section
      id="faq"
      ref={ref}
      className="grain relative isolate overflow-hidden bg-[hsl(0_0%_0%)] py-24 sm:py-28 lg:py-32"
    >
      <StarField count={35} className="-z-20 opacity-40" />
      <div
        aria-hidden
        className="absolute right-[-8%] top-1/3 -z-20 h-[520px] w-[620px] rounded-full bg-[radial-gradient(closest-side,hsl(230_90%_50%/0.13),transparent)] blur-3xl"
      />

      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-12">
        <div
          className={`transition-[opacity,transform] duration-700 ease-out lg:col-span-4 ${
            inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-primary-glow">
            FAQ
          </p>
          <h2 className="mt-6 font-display text-3xl font-light leading-tight tracking-tight text-foreground sm:text-4xl">
            Häufige Fragen,
            <br className="hidden sm:block" /> klar beantwortet.
          </h2>
          <div className="mt-7 h-px w-28 bg-gradient-hairline" aria-hidden />
          <p className="mt-7 max-w-sm text-sm font-light leading-relaxed text-muted-foreground">
            Noch etwas offen? Schreib uns – wir antworten meist am selben Tag.
          </p>

          <a
            href="#kontakt"
            className="group mt-8 inline-flex items-center gap-2.5 border border-border/70 px-4 py-2.5 font-display text-xs font-light tracking-wide text-foreground/85 transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-primary/60 hover:text-foreground"
          >
            <HelpCircle className="h-4 w-4 text-primary-glow" strokeWidth={1.5} />
            Frage stellen
          </a>
        </div>

        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((item, index) => (
              <AccordionItem
                key={item.q}
                value={`faq-${index}`}
                className={`border-border/60 transition-[opacity,transform] duration-700 ease-out ${
                  inView ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
                }`}
                style={{ transitionDelay: `${index * 70}ms` }}
              >
                <AccordionTrigger className="py-6 text-left font-display text-base font-light tracking-wide text-foreground/90 hover:text-foreground hover:no-underline sm:text-lg">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-sm font-light leading-relaxed text-muted-foreground">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
